import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import Client from '../Client';
import Config from '../ClientConfig';
import {
  formatQuantityForUnit,
  isWeightUnit,
  normalizeEstimatedStockInput,
} from './orderQuantityRules';
import { configureUnicodePdf } from './pdfUnicodeFont';

const DEFAULT_ALERT_TOLERANCE_PERCENT = 20;
const ORDER_PROPOSAL_LEAD_DAYS = 1;
const PLANNING_CELL_BACKGROUND = '#f3f8fc';
const PLANNING_HEADER_BACKGROUND = '#e8f2fa';
const QUANTITY_GUIDE = 'All quantities use the product unit. Suggested qty includes expected use until tomorrow, followed by the selected stock cover. Suggested and Order qty follow the supplier\'s order multiple—for example, 5 kg allows 5, 10, 15 kg.';

const BLOCKING_FLAGS = new Set([
  'stock_evidence_missing',
  'stock_baseline_unreliable',
  'usage_evidence_insufficient',
  'unit_basis_unsupported',
  'invalid_multiplier',
  'estimated_stock_unavailable',
  'usage_estimate_unavailable',
]);

const REVIEW_FLAGS = new Set([
  'negative_net_usage',
]);

const filterFieldSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiInputBase-input': {
    fontSize: { xs: '16px', sm: '14px' },
  },
  '& .MuiInputLabel-root': {
    fontSize: { xs: '13px', sm: '13px' },
  },
};

const filterBarSx = {
  px: { xs: 1, sm: 1.5 },
  py: { xs: 0.75, sm: 1 },
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 1fr', sm: '220px 260px auto' },
  gap: { xs: 1, sm: 1.25 },
  alignItems: 'end',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
};

const advancedFilterBarSx = {
  ...filterBarSx,
  gridTemplateColumns: { xs: '1fr 1fr', sm: '160px 180px 180px auto' },
  backgroundColor: 'rgba(246,244,239,0.45)',
};

const headerCellSx = (align, width) => ({
  padding: { xs: '8px 3px', sm: '7px 5px' },
  textAlign: align,
  whiteSpace: 'nowrap',
  width,
  fontSize: { xs: '13px', sm: '13px', md: '14px' },
  backgroundColor: '#f6f4ef',
});

const bodyCellSx = (align, width) => ({
  padding: { xs: '8px 3px', sm: '7px 5px' },
  textAlign: align,
  verticalAlign: 'top',
  whiteSpace: align === 'left' ? 'normal' : 'nowrap',
  width,
  fontSize: { xs: '13.5px', sm: '13px', md: '14px' },
  lineHeight: 1.35,
});

const mainSlotSx = align => ({
  minHeight: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: align === 'left' ? 'flex-start' : (align === 'center' ? 'center' : 'flex-end'),
});

export function createSimpleOrderProposalDraft() {
  return {
    filters: {
      location: '',
      supplier: '',
      asof: dayjs().format('YYYY-MM-DD'),
      lookbackDays: '28',
      coverageDays: '14',
      centralSupplyOnly: false,
    },
    meta: null,
    rows: [],
    adjustments: {},
    stockAdjustments: {},
    requestFilters: null,
    status: 'idle',
    dirty: false,
    errorText: '',
  };
}

const entityLabel = (entity, fallback = '') => {
  if (entity === null || entity === undefined) return fallback;
  if (typeof entity === 'string' || typeof entity === 'number') return String(entity);
  return String(entity.name || entity.label || entity.iref || entity.id || fallback);
};

const entityId = entity => String(entity?.iref ?? entity?.id ?? entity?.uid ?? '');

const safeSelectValue = (value, options) => {
  const selected = String(value || '');
  return selected && options.some(option => entityId(option) === selected) ? selected : '';
};

const rowKey = (row, index) => String(row?.productId ?? row?.product?.iref ?? index);
const rowFlags = row => (Array.isArray(row?.flags) ? row.flags.map(String) : []);
const hasBlockingFlag = row => rowFlags(row).some(flag => BLOCKING_FLAGS.has(flag));
const hasReviewFlag = row => rowFlags(row).some(flag => REVIEW_FLAGS.has(flag));
const stockKnown = row => row?.estimatedStockReliable === true && row?.estimatedStock !== null && row?.estimatedStock !== undefined;
const stockReliable = row => stockKnown(row) && row?.unitBasisSupported === true;
const proposalReviewSummary = rows => {
  const unknownCount = rows.filter(row => !stockKnown(row)).length;
  const unreliableCount = rows.filter(row => (
    stockKnown(row)
    && (row?.stockBaselineReliable === false || !stockReliable(row))
  )).length;
  const blockedCount = rows.filter(row => hasBlockingFlag(row)).length;
  const reviewCount = rows.filter(row => !hasBlockingFlag(row) && hasReviewFlag(row)).length;
  const affectedCount = rows.filter(row => (
    !stockKnown(row)
    || row?.stockBaselineReliable === false
    || !stockReliable(row)
    || hasBlockingFlag(row)
    || hasReviewFlag(row)
  )).length;
  if (!affectedCount) return null;

  const productCount = count => `${count} ${count === 1 ? 'product' : 'products'}`;
  const details = [
    unknownCount > 0 ? `${productCount(unknownCount)} ${unknownCount === 1 ? 'has' : 'have'} no safe stock estimate.` : '',
    unreliableCount > 0 ? `${productCount(unreliableCount)} ${unreliableCount === 1 ? 'has' : 'have'} a stock estimate that needs review.` : '',
    blockedCount > 0 ? `${productCount(blockedCount)} ${blockedCount === 1 ? 'has' : 'have'} no safe suggestion.` : '',
    reviewCount > 0 ? `${productCount(reviewCount)} ${reviewCount === 1 ? 'has' : 'have'} usage that needs review.` : '',
    'Review the highlighted rows. Full calculation details are available in CSV.',
  ].filter(Boolean).join(' ');

  return {
    affectedCount,
    label: `${affectedCount} ${affectedCount === 1 ? 'product needs' : 'products need'} review`,
    details,
  };
};
const proposalPlanningDetails = draft => {
  const meta = draft?.meta || {};
  const stockCoverDays = draft?.requestFilters?.coverageDays ?? '—';
  const totalTargetDays = meta.coverageDays ?? (
    Number.isFinite(Number(stockCoverDays))
      ? Number(stockCoverDays) + ORDER_PROPOSAL_LEAD_DAYS
      : '—'
  );
  return `Estimated stock through ${meta.stockCutoff || 'the prior day'}. Order date: tomorrow. Suggested qty covers ${ORDER_PROPOSAL_LEAD_DAYS} day until then plus ${stockCoverDays} days of stock cover (${totalTargetDays} days total). Usage: ${meta.historyStart || '—'}–${meta.historyEnd || '—'}. Outstanding orders are excluded.`;
};
const wholeQuantity = value => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= 0 ? numeric : null;
};
const multiplierFor = row => {
  const multiplier = Number(row?.quantityMultiplier);
  return Number.isInteger(multiplier) && multiplier > 0 ? multiplier : null;
};
export const adjustedStoredQuantity = (row, adjustedInput) => {
  const input = wholeQuantity(adjustedInput);
  const multiplier = multiplierFor(row);
  return input === null || multiplier === null ? null : input * multiplier;
};
const inputQuantityForStored = (row, storedQuantity) => {
  const stored = wholeQuantity(storedQuantity);
  const multiplier = multiplierFor(row);
  if (stored === null || multiplier === null || stored % multiplier !== 0) return null;
  return stored / multiplier;
};
const invalidOrderQuantity = (row, storedQuantity) => {
  if (storedQuantity === null || storedQuantity === undefined || storedQuantity === '') return false;
  return inputQuantityForStored(row, storedQuantity) === null;
};
const initialAdjustment = row => {
  const storedQuantity = wholeQuantity(row?.recommendedStoredQuantity);
  const quantity = storedQuantity === null
    ? adjustedStoredQuantity(row, row?.recommendedInputQuantity)
    : storedQuantity;
  return quantity === null || hasBlockingFlag(row) || !stockReliable(row) ? '' : String(quantity);
};
const adjustmentFor = (draft, row, index) => {
  const key = rowKey(row, index);
  return Object.prototype.hasOwnProperty.call(draft?.adjustments || {}, key)
    ? draft.adjustments[key]
    : initialAdjustment(row);
};
const estimatedStockFor = (draft, row, index) => {
  const adjusted = draft?.stockAdjustments?.[rowKey(row, index)];
  if (adjusted !== undefined && adjusted !== '') {
    const numeric = Number(adjusted);
    return Number.isFinite(numeric) ? numeric : null;
  }
  if (!stockReliable(row)) return null;
  const numeric = Number(row?.estimatedStock);
  return Number.isFinite(numeric) ? numeric : null;
};
const suggestedStoredFor = (draft, row, index) => {
  const stock = estimatedStockFor(draft, row, index);
  const expectedOut = row?.expectedOut === null || row?.expectedOut === undefined
    ? null
    : Number(row.expectedOut);
  const multiplier = multiplierFor(row);
  if (stock === null || expectedOut === null || !Number.isFinite(expectedOut) || multiplier === null || hasBlockingFlag(row)) return '';
  return String(Math.ceil(Math.max(0, expectedOut - stock) / multiplier) * multiplier);
};
const alertTolerancePercent = draft => {
  const rawValue = draft?.meta?.alertTolerancePercent;
  const configured = rawValue === null || rawValue === undefined || rawValue === ''
    ? Number.NaN
    : Number(rawValue);
  return Number.isFinite(configured) && configured >= 0 && configured <= 100
    ? configured
    : DEFAULT_ALERT_TOLERANCE_PERCENT;
};
export const quantityWarningFor = (draft, row, index) => {
  const adjusted = wholeQuantity(adjustmentFor(draft, row, index));
  const suggested = wholeQuantity(suggestedStoredFor(draft, row, index));
  const stock = estimatedStockFor(draft, row, index);
  const expectedOut = row?.expectedOut === null || row?.expectedOut === undefined
    ? null
    : Number(row.expectedOut);
  const multiplier = multiplierFor(row);
  const unit = unitLabel(row?.unit);
  if (multiplier !== null && invalidOrderQuantity(row, adjustmentFor(draft, row, index))) {
    return { severity: 'error', message: `Use multiples of ${multiplier || 1}${unit ? ` ${unit}` : ''}.`, mobile: 'Step' };
  }
  if (adjusted === null || suggested === null || stock === null || expectedOut === null || !Number.isFinite(expectedOut) || multiplier === null) return null;
  if (adjusted === suggested) return null;
  const rawNeed = Math.max(0, expectedOut - stock);
  const allowance = Math.max(multiplier, suggested)
    * alertTolerancePercent(draft)
    / 100;
  if (adjusted < suggested && rawNeed - adjusted > allowance + 1e-6) {
    return { severity: 'error', message: `Quantity may be too low. Suggested: ${suggested}${unit ? ` ${unit}` : ''}.`, mobile: 'Low' };
  }
  if (adjusted > suggested && adjusted - rawNeed > allowance + 1e-6) {
    return { severity: 'warning', message: `Quantity may be too high. Suggested: ${suggested}${unit ? ` ${unit}` : ''}.`, mobile: 'High' };
  }
  return null;
};
const unitLabel = value => ({ 0: 'pc', 1: 'kg', 3: 'other', 9: 'undefined' }[Number(value)] || '');
const formatWeeklyAverage = (value, fallback = '—') => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
};
export const buildPurchaseOrderDraftLines = (draft, rows) => {
  const sourceRows = Array.isArray(rows)
    ? rows
    : (Array.isArray(draft?.rows) ? draft.rows : []).filter(row => (
      !draft?.filters?.centralSupplyOnly || row?.centralSupply === true || row?.centralSupply === 1
    ));
  return sourceRows.reduce((lines, row, index) => {
    const adjustedStored = adjustmentFor(draft, row, index);
    const quantity = wholeQuantity(adjustedStored);
    if (quantity === null || quantity <= 0 || invalidOrderQuantity(row, adjustedStored)) return lines;
    const suggestedStored = suggestedStoredFor(draft, row, index);
    lines.push({
      product: row?.productName || row?.product?.name || row?.productId || '',
      estimatedStock: estimatedStockFor(draft, row, index),
      averageWeeklyUsage: row?.averageWeeklyUsage,
      suggestedQuantity: wholeQuantity(suggestedStored),
      quantity,
      unit: unitLabel(row?.unit),
      unitType: row?.unit,
    });
    return lines;
  }, []);
};
const hasInvalidOrderQuantity = (draft, rows) => rows.some((row, index) => {
  const adjusted = wholeQuantity(adjustmentFor(draft, row, index));
  return adjusted !== null && adjusted > 0 && invalidOrderQuantity(row, adjusted);
});
const requestMessage = (error, fallback) => {
  const body = error?.responseJSON;
  const direct = body?.detail || body?.error || body?.message;
  if (Array.isArray(direct)) return direct.join(' ');
  if (typeof direct === 'string') return direct;
  if (body && typeof body === 'object') {
    const fields = Object.entries(body).flatMap(([field, value]) => {
      const values = Array.isArray(value) ? value : [value];
      return values.filter(item => typeof item === 'string').map(item => `${field}: ${item}`);
    });
    if (fields.length) return fields.join(' ');
  }
  return fallback;
};
const csvText = value => {
  let text = String(value ?? '');
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

class OrderProposalUI extends React.Component {
  constructor(props) {
    super(props);
    this.client = props.client || new Client('none');
    this.state = {
      locations: [],
      suppliers: [],
      internalSupplier: '',
      loadingLookups: true,
      lookupError: '',
      moreSettings: false,
    };
    this._mounted = false;
    this._lookupSequence = 0;
    this._proposalSequence = 0;
    this._requests = [];
  }

  componentDidMount() {
    this._mounted = true;
    this.loadLookups();
  }

  componentWillUnmount() {
    this._mounted = false;
    this._lookupSequence += 1;
    this._proposalSequence += 1;
    this._requests.forEach(request => {
      if (request && typeof request.abort === 'function') request.abort();
    });
  }

  getDraft = () => this.props.draft || createSimpleOrderProposalDraft();

  setDraft = updater => {
    const current = this.getDraft();
    const next = typeof updater === 'function' ? updater(current) : updater;
    if (this.props.onDraftChange) this.props.onDraftChange(next);
  };

  loadLookups = () => {
    const sequence = ++this._lookupSequence;
    this.setState({ loadingLookups: true, lookupError: '' });
    const request = this.client.getDataAsync(
      Config.API_URL_PURCHASE_ORDER_RECOMMENDATION_FILTERS,
      {},
    );
    this._requests = [request];
    Promise.resolve(request).then(response => {
      if (!this._mounted || sequence !== this._lookupSequence) return;
      const locations = Array.isArray(response?.locations) ? response.locations : [];
      const suppliers = Array.isArray(response?.suppliers) ? response.suppliers : [];
      const issueText = Array.isArray(response?.issues)
        ? response.issues.map(issue => entityLabel(issue?.message || issue)).filter(Boolean).join(' ')
        : '';
      this.setDraft(previous => {
        const previousFilters = previous.filters || {};
        const location = safeSelectValue(previousFilters.location, locations);
        const supplier = safeSelectValue(previousFilters.supplier, suppliers);
        if (location === String(previousFilters.location || '') && supplier === String(previousFilters.supplier || '')) {
          return previous;
        }
        return {
          ...previous,
          filters: {
            ...previousFilters,
            location,
            supplier,
            centralSupplyOnly: supplier ? previousFilters.centralSupplyOnly : false,
          },
          dirty: previous.requestFilters ? true : previous.dirty,
          errorText: '',
        };
      });
      this.setState({
        locations,
        suppliers,
        internalSupplier: String(response?.internalSupplier ?? ''),
        loadingLookups: false,
        lookupError: issueText || (locations.length && suppliers.length ? '' : 'Some proposal filters are unavailable.'),
      });
    }).catch(error => {
      if (!this._mounted || sequence !== this._lookupSequence || error?.statusText === 'abort') return;
      this.setState({
        loadingLookups: false,
        lookupError: requestMessage(error, 'Could not load proposal filters.'),
      });
    });
  };

  updateFilter = (field, value, affectsRequest = true) => {
    this.setDraft(previous => ({
      ...previous,
      filters: { ...(previous.filters || {}), [field]: value },
      dirty: affectsRequest && previous.requestFilters ? true : previous.dirty,
      errorText: '',
    }));
  };

  updateSupplier = value => {
    const supplier = String(value || '');
    this.setDraft(previous => ({
      ...previous,
      filters: {
        ...(previous.filters || {}),
        supplier,
        centralSupplyOnly: !!supplier && supplier === this.state.internalSupplier,
      },
      dirty: previous.requestFilters ? true : previous.dirty,
      errorText: '',
    }));
  };

  validate = () => {
    const filters = this.getDraft().filters || {};
    if (!filters.location) return 'Select a stock location.';
    if (!filters.supplier) return 'Select a supplier.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(filters.asof || '') || !dayjs(filters.asof).isValid()) return 'Select a valid stock date.';
    if (dayjs(filters.asof).isAfter(dayjs(), 'day')) return 'Stock date cannot be in the future.';
    const history = Number(filters.lookbackDays);
    const target = Number(filters.coverageDays);
    if (!Number.isInteger(history) || history < 7 || history > 84) return 'Sales history must be 7–84 whole days.';
    if (!Number.isInteger(target) || target < 1 || target > 60 - ORDER_PROPOSAL_LEAD_DAYS) return 'Stock cover must be 1–59 whole days.';
    return '';
  };

  calculate = () => {
    const validation = this.validate();
    if (validation) {
      this.setDraft(previous => ({ ...previous, status: 'error', errorText: validation }));
      return;
    }
    const filters = this.getDraft().filters || {};
    const requestFilters = {
      location: String(filters.location),
      supplier: String(filters.supplier),
      asof: filters.asof,
      lookbackDays: Number(filters.lookbackDays),
      coverageDays: Number(filters.coverageDays),
    };
    const recommendationFilters = {
      ...requestFilters,
      coverageDays: requestFilters.coverageDays + ORDER_PROPOSAL_LEAD_DAYS,
    };
    const sequence = ++this._proposalSequence;
    this.setDraft(previous => ({ ...previous, status: 'loading', errorText: '' }));
    const request = this.client.getDataAsync(
      Config.API_URL_PURCHASE_ORDER_RECOMMENDATIONS,
      recommendationFilters,
    );
    this._requests = [request];
    Promise.resolve(request).then(response => {
      if (!this._mounted || sequence !== this._proposalSequence) return;
      if (!response || !Array.isArray(response.items)) throw new Error('Invalid recommendation response.');
      const rows = response.items.slice();
      const adjustments = {};
      const stockAdjustments = {};
      rows.forEach((row, index) => {
        adjustments[rowKey(row, index)] = initialAdjustment(row);
        stockAdjustments[rowKey(row, index)] = '';
      });
      this.setDraft(previous => ({
        ...previous,
        meta: response.meta || {},
        rows,
        adjustments,
        stockAdjustments,
        requestFilters,
        status: 'success',
        dirty: false,
        errorText: '',
      }));
    }).catch(error => {
      if (!this._mounted || sequence !== this._proposalSequence || error?.statusText === 'abort') return;
      this.setDraft(previous => ({
        ...previous,
        status: 'error',
        errorText: error instanceof Error ? error.message : requestMessage(error, 'Could not calculate the proposal.'),
      }));
    });
  };

  visibleRows = () => {
    const draft = this.getDraft();
    const rows = Array.isArray(draft.rows) ? draft.rows : [];
    return draft.filters?.centralSupplyOnly
      ? rows.filter(row => row?.centralSupply === true || row?.centralSupply === 1)
      : rows;
  };

  updateAdjustment = (row, index, value) => {
    if (!/^\d*$/.test(value)) return;
    const key = rowKey(row, index);
    this.setDraft(previous => ({
      ...previous,
      adjustments: { ...(previous.adjustments || {}), [key]: value },
    }));
  };

  updateStockAdjustment = (row, index, value) => {
    const normalized = normalizeEstimatedStockInput(value, row?.unit);
    if (normalized === null) return;
    const key = rowKey(row, index);
    this.setDraft(previous => ({
      ...previous,
      stockAdjustments: { ...(previous.stockAdjustments || {}), [key]: normalized },
    }));
  };

  resetAdjustments = () => {
    this.setDraft(previous => {
      const adjustments = {};
      const stockAdjustments = {};
      (previous.rows || []).forEach((row, index) => {
        adjustments[rowKey(row, index)] = initialAdjustment(row);
        stockAdjustments[rowKey(row, index)] = '';
      });
      return { ...previous, adjustments, stockAdjustments };
    });
  };

  clearAdjustments = () => {
    this.setDraft(previous => {
      const adjustments = {};
      (previous.rows || []).forEach((row, index) => { adjustments[rowKey(row, index)] = ''; });
      return { ...previous, adjustments };
    });
  };

  exportCsv = () => {
    const draft = this.getDraft();
    const rows = this.visibleRows();
    if (!rows.length || draft.dirty) return;
    const meta = draft.meta || {};
    const headers = [
      'Product ID', 'Product', 'Unit', 'Ledger stock', 'Estimated stock', 'Adjusted estimated stock', 'Estimated stock reliable',
      'Avg usage/week', 'Usage in window', 'Usage source', 'Usage confidence', 'Expected out', 'Raw shortfall', 'Adjusted raw shortfall',
      'Multiplier', 'Suggested input', 'Suggested stored', 'Adjusted input', 'Adjusted stored',
      'Preferred', 'Central supply', 'Order compatible', 'Flags',
    ];
    const lines = [
      ['Order Proposal - Draft', ''],
      ['Supplier', entityLabel(meta.supplier, draft.requestFilters?.supplier)],
      ['Location', entityLabel(meta.location, draft.requestFilters?.location)],
      ['Stock date', meta.asof || draft.requestFilters?.asof || ''],
      ['Stock cutoff', meta.stockCutoff || ''],
      ['Sales history', `${meta.historyStart || ''} - ${meta.historyEnd || ''}`],
      ['Order lead days', ORDER_PROPOSAL_LEAD_DAYS],
      ['Stock cover after order date', draft.requestFilters?.coverageDays ?? ''],
      ['Total target days', meta.coverageDays ?? ''],
      ['Outstanding orders included', meta.openOrdersIncluded === true ? 'Yes' : 'No'],
      ['', ''],
      headers,
      ...rows.map((row, index) => {
        const adjustedStored = wholeQuantity(
          this.getDraft().adjustments?.[rowKey(row, index)] ?? initialAdjustment(row),
        );
        const adjustedInput = inputQuantityForStored(row, adjustedStored);
        const adjustedStock = estimatedStockFor(draft, row, index);
        const suggestedStored = suggestedStoredFor(draft, row, index);
        const suggestedInput = inputQuantityForStored(row, suggestedStored);
        const expectedOut = row?.expectedOut === null || row?.expectedOut === undefined
          ? null
          : Number(row.expectedOut);
        const adjustedRawShortfall = (
          adjustedStock !== null
          && expectedOut !== null
          && Number.isFinite(expectedOut)
        ) ? Math.max(0, expectedOut - adjustedStock) : '';
        return [
          row?.productId, row?.productName || row?.product?.name, unitLabel(row?.unit), row?.currentStock,
          row?.estimatedStock, adjustedStock, row?.estimatedStockReliable === true ? 'Yes' : 'No',
          row?.averageWeeklyUsage, row?.usageInWindow, row?.usageSource, row?.usageConfidence, row?.expectedOut, row?.rawShortfall, adjustedRawShortfall,
          row?.quantityMultiplier, suggestedInput, suggestedStored,
          adjustedInput, adjustedStored, row?.preferred === true ? 'Yes' : 'No', row?.centralSupply === true ? 'Yes' : 'No',
          row?.orderFormCompatible === true ? 'Yes' : 'No', rowFlags(row).join(' | '),
        ];
      }),
    ].map(row => row.map(csvText).join(','));
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `order_proposal_${draft.requestFilters?.asof || 'date'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  exportPdf = async () => {
    const draft = this.getDraft();
    const rows = this.visibleRows();
    const draftLines = buildPurchaseOrderDraftLines(draft, rows);
    if (!draftLines.length || draft.dirty || hasInvalidOrderQuantity(draft, rows)) return;
    const meta = draft.meta || {};
    const supplier = entityLabel(meta.supplier, draft.requestFilters?.supplier || '');
    const location = entityLabel(meta.location, draft.requestFilters?.location || '');
    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const proposalDate = meta.asof || draft.requestFilters?.asof || draft.filters?.asof || '';
    const margin = 34;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    let pdfFont;
    try {
      pdfFont = await configureUnicodePdf(doc);
    } catch (error) {
      console.error('Could not load the Unicode PDF font.', error);
      window.alert('The PDF font could not be loaded. Please try again.');
      return;
    }
    doc.setFontSize(15);
    doc.text('Purchase Order Draft - Not Placed', margin, 34);
    doc.setFontSize(9);
    const metadata = [
      `Supplier: ${supplier}`,
      `Location: ${location}`,
      `Prepared: ${generatedAt}`,
      `Stock date: ${proposalDate}`,
    ];
    let metadataY = 51;
    metadata.forEach(line => {
      doc.text(line, margin, metadataY);
      metadataY += 12;
    });
    autoTable(doc, {
      startY: metadataY + 10,
      head: [['Product', 'Est. stock', 'Avg/week', 'Suggested', 'Order qty', 'Unit']],
      body: draftLines.map(line => [
        line.product,
        formatQuantityForUnit(line.estimatedStock, line.unitType, '—'),
        formatWeeklyAverage(line.averageWeeklyUsage),
        formatQuantityForUnit(line.suggestedQuantity, line.unitType, '—'),
        formatQuantityForUnit(line.quantity, line.unitType, '—'),
        line.unit,
      ]),
      margin: { left: margin, right: margin, bottom: 34 },
      theme: 'grid',
      styles: { font: pdfFont, fontStyle: 'normal', fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [39, 52, 66], fontStyle: 'normal' },
      columnStyles: {
        1: { halign: 'right', cellWidth: 76 },
        2: { halign: 'right', cellWidth: 76 },
        3: { halign: 'right', cellWidth: 76 },
        4: { halign: 'right', cellWidth: 76 },
        5: { cellWidth: 45 },
      },
    });
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setFont(pdfFont, 'normal');
      doc.setFontSize(8);
      doc.text('Draft only - verify quantities before ordering.', margin, doc.internal.pageSize.getHeight() - 16);
      doc.text(
        `Page ${page} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - margin,
        doc.internal.pageSize.getHeight() - 16,
        { align: 'right' },
      );
    }
    doc.save(`order_proposal_${draft.requestFilters?.asof || 'date'}.pdf`);
  };

  render() {
    const draft = this.getDraft();
    const filters = draft.filters || {};
    const rows = this.visibleRows();
    const loading = draft.status === 'loading';
    const canExportCsv = rows.length > 0 && !draft.dirty && !loading;
    const canExportPdf = canExportCsv
      && !hasInvalidOrderQuantity(draft, rows)
      && buildPurchaseOrderDraftLines(draft, rows).length > 0;
    const reviewSummary = proposalReviewSummary(rows);
    const planningDetails = !this.state.loadingLookups && draft.meta && !loading && !draft.dirty
      ? proposalPlanningDetails(draft)
      : '';
    const locationValue = safeSelectValue(filters.location, this.state.locations);
    const supplierValue = safeSelectValue(filters.supplier, this.state.suppliers);
    return (
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={filterBarSx}>
          <TextField select required variant="standard" size="small" label="Location" value={locationValue} onChange={event => this.updateFilter('location', event.target.value)} disabled={loading || this.state.loadingLookups} sx={filterFieldSx}>
            <MenuItem value="">Select location</MenuItem>
            {this.state.locations.map(location => {
              const id = entityId(location);
              return <MenuItem key={id} value={id}>{entityLabel(location, id)}</MenuItem>;
            })}
          </TextField>
          <TextField select required variant="standard" size="small" label="Supplier" value={supplierValue} onChange={event => this.updateSupplier(event.target.value)} disabled={loading || this.state.loadingLookups} sx={filterFieldSx}>
            <MenuItem value="">Select supplier</MenuItem>
            {this.state.suppliers.map(supplier => {
              const id = entityId(supplier);
              return <MenuItem key={id} value={id}>{entityLabel(supplier, id)}</MenuItem>;
            })}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-start' }, gap: 0.75, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
            <Button size="small" variant="text" onClick={() => this.setState(previous => ({ moreSettings: !previous.moreSettings }))}>{this.state.moreSettings ? 'Hide settings' : 'More settings'}</Button>
            {!!filters.centralSupplyOnly && !this.state.moreSettings && <Chip size="small" variant="outlined" label="Central only" />}
            <Button size="small" variant="contained" onClick={this.calculate} disabled={loading || this.state.loadingLookups}>{loading ? 'Calculating…' : 'Calculate'}</Button>
          </Box>
        </Box>

        {this.state.moreSettings && (
          <Box sx={advancedFilterBarSx}>
            <TextField variant="standard" size="small" type="date" label="Stock date" value={filters.asof || ''} onChange={event => this.updateFilter('asof', event.target.value)} InputLabelProps={{ shrink: true }} sx={filterFieldSx} />
            <TextField variant="standard" size="small" type="number" label="History (days)" value={filters.lookbackDays || ''} onChange={event => this.updateFilter('lookbackDays', event.target.value)} inputProps={{ min: 7, max: 84, step: 1 }} sx={filterFieldSx} />
            <Tooltip title="Desired stock cover after tomorrow's order date."><TextField variant="standard" size="small" type="number" label="Stock cover (days)" value={filters.coverageDays || ''} onChange={event => this.updateFilter('coverageDays', event.target.value)} inputProps={{ min: 1, max: 59, step: 1 }} sx={filterFieldSx} /></Tooltip>
            <FormControlLabel control={<Checkbox checked={!!filters.centralSupplyOnly} onChange={event => this.updateFilter('centralSupplyOnly', event.target.checked, false)} />} label="Central supply only" />
          </Box>
        )}

        {!!this.state.lookupError && <Typography variant="caption" color="warning.dark" sx={{ px: 1, py: 0.4 }}>{this.state.lookupError}</Typography>}
        {!!draft.errorText && <Typography variant="caption" color="error" sx={{ px: 1, py: 0.4 }}>{draft.errorText}</Typography>}
        {draft.dirty && draft.requestFilters && <Typography variant="caption" color="warning.dark" sx={{ px: 1, py: 0.4 }}>Filters changed. Calculate again before export.</Typography>}

        <Stack direction="row" alignItems="center" sx={{ flexWrap: { xs: 'nowrap', sm: 'wrap' }, gap: { xs: 0.25, sm: 0.5 }, px: 0.75, py: 0.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="subtitle2" sx={{ display: { xs: 'none', sm: 'block' } }}>{rows.length} {rows.length === 1 ? 'product' : 'products'}</Typography>
          <Tooltip title={QUANTITY_GUIDE} arrow describeChild enterTouchDelay={0} leaveTouchDelay={5000}>
            <Button
              size="small"
              variant="text"
              aria-label="Quantity guide"
              sx={{ minWidth: { xs: 32, sm: 0 }, px: 0.5, gap: 0.4, textTransform: 'none' }}
            >
              <InfoOutlinedIcon fontSize="small" />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Quantities</Box>
            </Button>
          </Tooltip>
          {!!planningDetails && (
            <Tooltip title={planningDetails} arrow describeChild enterTouchDelay={0} leaveTouchDelay={5000}>
              <Button
                size="small"
                variant="text"
                aria-label="Planning details"
                sx={{ minWidth: { xs: 32, sm: 0 }, px: 0.5, gap: 0.4, textTransform: 'none' }}
              >
                <CalendarMonthOutlinedIcon fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Plan details</Box>
              </Button>
            </Tooltip>
          )}
          {reviewSummary && (
            <Tooltip title={reviewSummary.details} arrow describeChild enterTouchDelay={0} leaveTouchDelay={5000}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<WarningAmberOutlinedIcon sx={{ display: { xs: 'none', sm: 'inherit' } }} />}
                label={(
                  <>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{reviewSummary.affectedCount} review</Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{reviewSummary.label}</Box>
                  </>
                )}
                tabIndex={0}
                aria-label={`${reviewSummary.label}. ${reviewSummary.details}`}
                sx={{ '& .MuiChip-label': { px: { xs: 0.75, sm: 1.5 } } }}
              />
            </Tooltip>
          )}
          <Tooltip title="Adjustments stay on this page; exports do not place an order"><Chip size="small" variant="outlined" label="Draft only" sx={{ display: { xs: 'none', sm: 'inline-flex' } }} /></Tooltip>
          <Button size="small" variant="text" onClick={this.resetAdjustments} disabled={!draft.rows?.length} sx={{ minWidth: { xs: 0, sm: 64 }, px: { xs: 0.5, sm: 1 } }}>Reset</Button>
          <Button size="small" variant="text" onClick={this.clearAdjustments} disabled={!draft.rows?.length} sx={{ minWidth: { xs: 0, sm: 64 }, px: { xs: 0.5, sm: 1 } }}>Clear</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" onClick={this.exportCsv} disabled={!canExportCsv} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>CSV</Button>
          <Button size="small" variant="outlined" onClick={this.exportPdf} disabled={!canExportPdf} sx={{ minWidth: { xs: 0, sm: 64 }, px: { xs: 0.75, sm: 1.25 } }}>PDF</Button>
        </Stack>

        <TableContainer sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table stickyHeader size="small" sx={{ minWidth: { xs: 0, sm: 760 }, width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx('left'), width: { xs: '35%', sm: '27%' } }}><b>Product</b></TableCell>
                <TableCell sx={{ ...headerCellSx('right'), width: { xs: '21%', sm: '13%' }, bgcolor: PLANNING_HEADER_BACKGROUND }}><b>Est. stock</b></TableCell>
                <TableCell sx={{ ...headerCellSx('right', '15%'), display: { xs: 'none', sm: 'table-cell' } }}><b>Avg/week</b></TableCell>
                <TableCell sx={{ ...headerCellSx('right'), width: { xs: '20%', sm: '13%' }, bgcolor: PLANNING_HEADER_BACKGROUND }}><b>Suggested<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> qty</Box></b></TableCell>
                <TableCell sx={{ ...headerCellSx('right'), width: { xs: '24%', sm: '15%' } }}><b>Order qty</b></TableCell>
                <TableCell sx={{ ...headerCellSx('left', '17%'), display: { xs: 'none', sm: 'table-cell' } }}><b>Status</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const key = rowKey(row, index);
                const reliable = stockReliable(row);
                const blocked = hasBlockingFlag(row);
                const adjustedStored = draft.adjustments?.[key] ?? initialAdjustment(row);
                const adjustedStock = draft.stockAdjustments?.[key] ?? '';
                const displayedStock = adjustedStock !== ''
                  ? adjustedStock
                  : (stockKnown(row) ? formatQuantityForUnit(row?.estimatedStock, row?.unit) : '');
                const suggestedStored = suggestedStoredFor(draft, row, index);
                const quantityWarning = quantityWarningFor(draft, row, index);
                const multiplier = multiplierFor(row);
                const planningCellBackground = quantityWarning || !reliable || blocked
                  ? 'inherit'
                  : PLANNING_CELL_BACKGROUND;
                const status = !stockKnown(row) ? 'No estimate' : (!reliable || blocked ? 'Needs review' : (hasReviewFlag(row) ? 'Check usage' : 'Ready'));
                return (
                  <TableRow key={key} hover sx={{ bgcolor: quantityWarning?.severity === 'error' ? '#ffebee' : (!reliable || blocked || quantityWarning ? '#fff8e1' : undefined) }}>
                    <TableCell sx={{ ...bodyCellSx('left'), width: { xs: '35%', sm: '27%' }, wordBreak: 'break-word' }}>
                      <Box sx={mainSlotSx('left')}>{row?.productName || row?.product?.name || row?.productId}</Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {unitLabel(row?.unit)}
                        {multiplier > 1 && (
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> · Order multiple: {multiplier} {unitLabel(row?.unit)}</Box>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx('right'), width: { xs: '21%', sm: '13%' }, bgcolor: planningCellBackground }}>
                      <Box sx={mainSlotSx('right')}>
                        <TextField
                          size="small"
                          value={displayedStock}
                          placeholder="—"
                          onChange={event => this.updateStockAdjustment(row, index, event.target.value)}
                          inputProps={{
                            step: isWeightUnit(row?.unit) ? '0.001' : '1',
                            inputMode: isWeightUnit(row?.unit) ? 'decimal' : 'numeric',
                            pattern: isWeightUnit(row?.unit)
                              ? '^-?[0-9]+([.,][0-9]{0,3})?$'
                              : '^-?[0-9]+$',
                            style: { textAlign: 'right' },
                            'aria-label': `Estimated stock for ${row?.productName || key}`,
                          }}
                          sx={{ width: { xs: '100%', sm: 78 }, maxWidth: '100%', minWidth: 0 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx('right', '15%'), display: { xs: 'none', sm: 'table-cell' } }}><Box sx={mainSlotSx('right')}>{formatWeeklyAverage(row?.averageWeeklyUsage)}</Box></TableCell>
                    <TableCell sx={{ ...bodyCellSx('right'), width: { xs: '20%', sm: '13%' }, fontWeight: 600, bgcolor: planningCellBackground }}>
                      <Box sx={mainSlotSx('right')}>{suggestedStored === '' ? '—' : formatQuantityForUnit(suggestedStored, row?.unit, '—')}</Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx('right'), width: { xs: '24%', sm: '15%' } }}>
                      <Box sx={mainSlotSx('right')}>
                        <TextField size="small" value={adjustedStored} error={quantityWarning?.severity === 'error'} onChange={event => this.updateAdjustment(row, index, event.target.value)} inputProps={{ min: 0, step: multiplier || 1, inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'right' }, 'aria-label': `Order quantity for ${row?.productName || key}` }} sx={{ width: { xs: '100%', sm: 78 }, maxWidth: '100%', minWidth: 0 }} />
                      </Box>
                      {quantityWarning && (
                        <Typography variant="caption" color={quantityWarning.severity === 'error' ? 'error' : 'warning.dark'} sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{quantityWarning.message}</Box>
                          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{quantityWarning.mobile}</Box>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx('left', '17%'), display: { xs: 'none', sm: 'table-cell' } }}><Box sx={mainSlotSx('left')}><Chip size="small" variant={status === 'Ready' ? 'outlined' : 'filled'} color={status === 'Ready' ? 'success' : 'warning'} label={status} /></Box></TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && !loading && <TableRow><TableCell colSpan={6} align="center">{draft.status === 'success' ? 'No products match this proposal.' : 'Select filters and calculate a proposal.'}</TableCell></TableRow>}
            </TableBody>
          </Table>
          {(loading || this.state.loadingLookups) && <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={28} /></Box>}
        </TableContainer>
      </Box>
    );
  }
}

export default OrderProposalUI;
